import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DESKTOP_INITIAL, MOBILE_INITIAL, getInitialVisible, matchesCountrySearch } from "./Detailconstants.js";
import { fetchContent } from "../../../../services/contentService";
import ContactModal from "./ContactModal.jsx";

function getEmbassyBadgeClass(type) {
  const map = {
    Embassy: "VF-badge--embassy",
    Consulate: "VF-badge--consulate",
    "High Commission": "VF-badge--high-commission",
    "Deputy High Commission": "VF-badge--fro",
    "Consulate-General": "VF-badge--consulate",
  };
  return map[type] || "VF-badge--embassy";
}

/* ================================================================
   EMBASSY CARD
   ================================================================ */
function EmbassyCard({ embassy, index }) {
  return (
    <article
      className="VF-embassy-card"
      data-aos="fade-up"
      data-aos-delay={Math.min(index * 60, 300)}
      aria-label={embassy.embassy_name ?? "Embassy"}
    >
      <div className="VF-embassy-card__header">
        <div>
          <div className="VF-embassy-card__country-code">{embassy.country ?? ""}</div>
          <h3 className="VF-embassy-card__name">{embassy.embassy_name ?? ""}</h3>
          <p className="VF-embassy-card__city">{embassy.city ?? ""}, India</p>
        </div>
        <span className={`VF-badge ${getEmbassyBadgeClass(embassy.embassy_type)}`}>{embassy.embassy_type}</span>
      </div>

      <div className="VF-embassy-card__body">
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
          </svg>
          <span className="VF-info-row__text">{embassy.address ?? ""}</span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.76a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z"/>
          </svg>
          <span className="VF-info-row__text">
            <a href={`tel:${embassy.phone ?? ""}`}>{embassy.phone ?? ""}</a>
          </span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
          <span className="VF-info-row__text">
            <a href={`mailto:${embassy.email ?? ""}`}>{embassy.email ?? ""}</a>
          </span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span className="VF-info-row__text">{embassy.working_hours ?? ""}</span>
        </div>
        {(embassy.visa_types?.length > 0) && (
          <div className="VF-embassy-card__visas">
            <p className="VF-embassy-card__visas-label">Visa Categories</p>
            <div className="VF-office-card__services" aria-label="Visa categories">
              {embassy.visa_types.map((vt) => (
                <span key={vt} className="VF-service-tag">{vt}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="VF-embassy-card__footer">
        <a href={embassy.website ?? "#"} target="_blank" rel="noopener noreferrer" className="VF-card-link VF-card-link--primary">
          Official Website
        </a>
        <a href={`mailto:${embassy.email ?? ""}`} className="VF-card-link VF-card-link--secondary">
          Send Email
        </a>
      </div>
    </article>
  );
}

/* ================================================================
   EMBASSIES SECTION
   ================================================================ */
export default function EmbassiesSection() {
  const [embassies, setEmbassies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [visibleCount, setVisibleCount] = useState(getInitialVisible);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchContent("visaDetail").then(section => {
      const list = section?.content?.embassies;
      if (Array.isArray(list) && list.length) {
        setEmbassies(list.map((e, idx) => ({ ...e, id: e.id || `embassy-${idx}` })));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const countryOptions = useMemo(() => {
    const unique = [...new Set(embassies.map((e) => e.country))].sort();
    return unique;
  }, [embassies]);

  const typeOptions = useMemo(() => {
    const unique = [...new Set(embassies.map((e) => e.embassy_type))].sort();
    return unique;
  }, [embassies]);

  const embassyStates = useMemo(() => {
    const unique = [...new Set(embassies.map((e) => e.state).filter(Boolean))].sort();
    return unique;
  }, [embassies]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return embassies.filter((emb) => {
      const matchSearch =
        !q ||
        (emb.embassy_name?.toLowerCase().includes(q) ?? false) ||
        matchesCountrySearch(emb.country, q) ||
        (emb.city?.toLowerCase().includes(q) ?? false) ||
        (emb.address?.toLowerCase().includes(q) ?? false) ||
        ((emb.visa_types || [])?.some((v) => v?.toLowerCase().includes(q)) ?? false);
      const matchCountry = !selectedCountry || emb.country === selectedCountry;
      const matchType = !selectedType || emb.embassy_type === selectedType;
      const matchState = !selectedState || emb.state === selectedState;
      return matchSearch && matchCountry && matchType && matchState;
    });
  }, [search, selectedCountry, selectedType, selectedState, embassies]);

  const isOutOfScope = search.trim().length > 0 && filtered.length === 0;

  const handleReset = useCallback(() => {
    setSearch("");
    setSelectedCountry("");
    setSelectedType("");
    setSelectedState("");
    setVisibleCount(getInitialVisible());
  }, []);

  const hasFilters = search || selectedCountry || selectedType || selectedState;
  const visibleCards = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const canCollapse = visibleCount > getInitialVisible() && !hasMore;

  const handleViewMore = () => setVisibleCount((p) => p + (window.innerWidth <= 767 ? MOBILE_INITIAL : DESKTOP_INITIAL));
  const handleCollapse = () => setVisibleCount(getInitialVisible());

  return (
    <section className="VF-section VF-section--white" id="embassies" aria-label="Embassies and Consulates Section">
      <div className="VF-container">
        <header className="VF-section-header" data-aos="fade-up">
          <h2 className="VF-heading-lg">Embassies &amp; Consulates</h2>
          <span className="VF-divider" aria-hidden="true" />
          <p>Directory of foreign embassies, high commissions and consulates located in India. Find official addresses, visa application contacts and operating hours.</p>
        </header>

        <div className="VF-filter-bar" data-aos="fade-up" data-aos-delay="80" role="search" aria-label="Filter embassies">
          <div className="VF-filter-group VF-filter-group--search">
            {/* <label className="VF-filter-label" htmlFor="VF-emb-search">Search</label> */}
            <input
              id="VF-emb-search"
              type="text"
              className="VF-filter-input"
              placeholder="Search by country, city, embassy name or visa type..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(getInitialVisible()); }}
              aria-label="Search embassies"
            />
          </div>
          <div className="VF-filter-row VF-filter-row--3col">
            <div className="VF-filter-group">
              {/* <label className="VF-filter-label" htmlFor="VF-emb-country">Country</label> */}
              <select id="VF-emb-country" className="VF-filter-select" value={selectedCountry} onChange={(e) => { setSelectedCountry(e.target.value); setVisibleCount(getInitialVisible()); }}>
                <option value="">Country</option>
                {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="VF-filter-group">
              {/* <label className="VF-filter-label" htmlFor="VF-emb-type">Mission Type</label> */}
              <select id="VF-emb-type" className="VF-filter-select" value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setVisibleCount(getInitialVisible()); }}>
                <option value="">Type</option>
                {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="VF-filter-group">
              {/* <label className="VF-filter-label" htmlFor="VF-emb-state">City / State</label> */}
              <select id="VF-emb-state" className="VF-filter-select" value={selectedState} onChange={(e) => { setSelectedState(e.target.value); setVisibleCount(getInitialVisible()); }}>
                <option value="">State</option>
                {embassyStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && (
            <button className="VF-filter-reset" onClick={handleReset}>Reset Filters</button>
          )}
        </div>

        <div className="VF-results-info" aria-live="polite" aria-atomic="true">
          <p className="VF-results-count">
            Showing <strong>{Math.min(visibleCount, filtered.length)}</strong> of <strong>{filtered.length}</strong> diplomatic missions
          </p>
        </div>

        {loading ? (
          <div className="VF-empty-state" role="status"><p className="VF-empty-state__text">Loading diplomatic missions...</p></div>
        ) : filtered.length > 0 ? (
          <>
            <div className="VF-cards-grid" role="list">
              {visibleCards.map((emb, i) => (
                <div key={emb.id} role="listitem">
                  <EmbassyCard embassy={emb} index={i} />
                </div>
              ))}
            </div>
            {(hasMore || canCollapse) && (
              <div className="VF-view-more-wrapper">
                {hasMore && (
                  <button className="VF-btn-view-more" onClick={handleViewMore}>
                    Show More Embassies
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    {/* <span className="VF-show-more-count">({filtered.length - visibleCount} more)</span> */}
                  </button>
                )}
                {canCollapse && (
                  <button className="VF-btn-view-more VF-btn-view-more--collapse" onClick={handleCollapse}>
                    Show Less
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="VF-empty-state" data-aos="fade-up" role="status">
            <h3 className="VF-empty-state__title">No diplomatic missions found</h3>
            <p className="VF-empty-state__text">
              {isOutOfScope
                ? "We couldn't find an embassy or consulate matching your search. Submit an enquiry and we'll assist you directly."
                : "Try adjusting your search or resetting the filters."}
            </p>
            <div className="VF-empty-state__actions">
              <button className="VF-btn-secondary" onClick={handleReset}>Reset Filters</button>
              {isOutOfScope && (
                <button className="VF-btn-primary" onClick={() => setModalOpen(true)}>Submit Enquiry</button>
              )}
            </div>
          </div>
        )}
      </div>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} query={search} sectionName="Embassies & Consulates" />
    </section>
  );
}